import { convert, DateTimeFormatter, Instant, LocalDate } from "@js-joda/core";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { Group } from "@visx/group";
import { LegendOrdinal } from "@visx/legend";
import { scaleBand, scaleLinear, scaleOrdinal } from "@visx/scale";
import { BarGroup, BarStack } from "@visx/shape";

import { type FC, type RefObject, useMemo, useRef } from "react";
import { Platform, PostStatus, type PostSuccessDto, sum } from "shared";
import { useResizeObserver } from "usehooks-ts";
import "./BarStackChartPosts.css";
import { localPoint } from "@visx/event";
import type { SeriesPoint } from "@visx/shape/lib/types";
import { defaultStyles, useTooltip, useTooltipInPortal } from "@visx/tooltip";

type Props = {
	posts: PostSuccessDto;
};

type CustomBarGroup = {
	date: string;
	failed: PostSuccessDto[string][PostStatus.FAILED];
	published: PostSuccessDto[string][PostStatus.PUBLISHED];
};

type CustomBarStack = {
	status: PostStatus;
} & Partial<Record<Platform, number>>;

type TooltipData = {
	bar: SeriesPoint<CustomBarStack>;
	key: Platform;
	index: number;
	height: number;
	width: number;
	x: number;
	y: number;
	color: string;
};

const margin = { top: 20, right: 30, bottom: 50, left: 40 };
const dateFormatter = DateTimeFormatter.ofPattern("yyyy-MM-dd");

const platformColorMap: Record<Platform, string> = {
	[Platform.FACEBOOK]: "#cfdcf0",
	[Platform.INSTAGRAM]: "#f9cdcd",
	[Platform.THREADS]: "#d7c5e2",
	[Platform.LINKEDIN]: "#0077b5",
	[Platform.WORDPRESS]: "#eee",
	[Platform.CUSTOM_BLOG]: "#eee",
	[Platform.GOOGLE_BUSINESS]: "#4285F4",
	[Platform.YOUTUBE]: "#FF0000",
	[Platform.TIKTOK]: "#000000",
	[Platform.X]: "#1DA1F2",
};

const statusMap: Record<PostStatus.PUBLISHED | PostStatus.FAILED, string> = {
	[PostStatus.PUBLISHED]: "Successful",
	[PostStatus.FAILED]: "Failed",
};

export const BarStackChartPosts: FC<Readonly<Props>> = ({ posts }) => {
	const containerRef = useRef<HTMLDivElement>(
		null,
	) as RefObject<HTMLDivElement>;
	const { width = 0, height = 0 } = useResizeObserver({ ref: containerRef });
	const {
		tooltipOpen,
		tooltipLeft,
		tooltipTop,
		tooltipData,
		hideTooltip,
		showTooltip,
	} = useTooltip<TooltipData>();

	const { containerRef: tooltipRef, TooltipInPortal } = useTooltipInPortal({
		// TooltipInPortal is rendered in a separate child of <body /> and positioned
		// with page coordinates which should be updated on scroll. consider using
		// Tooltip or TooltipWithBounds if you don't need to render inside a Portal
		scroll: true,
	});

	const tooltipStyles = useMemo(
		() => ({
			...defaultStyles,
			minWidth: 60,
			backgroundColor: "rgba(0,0,0,0.9)",
			color: "white",
		}),
		[],
	);

	const maxValue = useMemo(() => {
		let max = 0;
		for (const post of Object.values(posts)) {
			for (const status of Object.values(post)) {
				const values = Object.values(status) as number[];
				const localMax = sum(...values);
				if (localMax > max) {
					max = localMax;
				}
			}
		}

		return max;
	}, [posts]);

	const maxValueRange = useMemo(
		() =>
			Object.values(posts).reduce<
				Record<PostStatus.PUBLISHED | PostStatus.FAILED, number>
			>(
				(acc, statusValue) => {
					for (const [status, paltformValues] of Object.entries(
						statusValue,
					) as [
						PostStatus.PUBLISHED | PostStatus.FAILED,
						Record<Platform, number>,
					][]) {
						let maxStatus = 0;
						for (const value of Object.values(paltformValues)) {
							maxStatus += value ?? 0;
						}
						acc[status] = Math.max(acc[status], maxStatus);
					}

					return acc;
				},
				{ published: 0, failed: 0 },
			),
		[posts],
	);

	maxValueRange.failed *= -1;

	const platforms = useMemo(
		() =>
			Object.values(posts).reduce<Set<Platform>>((acc, status) => {
				for (const statusValue of Object.values(status)) {
					for (const platformValues of Object.keys(statusValue) as Platform[]) {
						acc.add(platformValues);
					}
				}
				return acc;
			}, new Set()),
		[posts],
	);
	const dates = useMemo(
		() =>
			Object.keys(posts).toSorted((a, b) =>
				LocalDate.parse(a).isBefore(LocalDate.parse(b)) ? -1 : 1,
			),
		[posts],
	);

	const colorScaleGroup = scaleOrdinal<PostStatus, string>({
		domain: [PostStatus.FAILED, PostStatus.PUBLISHED],
		range: ["#e41a1c", "#377eb8"],
	});
	const colorScale = scaleOrdinal<Platform, string>({
		domain: Array.from(platforms),
		range: Array.from(platforms).map((platform) => platformColorMap[platform]),
	});

	const xScale = useMemo(
		() =>
			scaleBand({
				domain: dates.map((date) =>
					convert(LocalDate.parse(date, dateFormatter)).toDate(),
				),
				range: [margin.left, width - margin.right],
			}),
		[width, dates],
	);

	const xScaleStatus = useMemo(
		() =>
			scaleBand({
				domain: [PostStatus.PUBLISHED, PostStatus.FAILED],
				range: [margin.left, width - margin.right],
			}),
		[width],
	);

	const yMax = useMemo(
		() =>
			scaleLinear({
				range: [height - margin.top, margin.top],
				domain: [0, maxValue],
			}),
		[maxValue, height],
	);

	const legendScale = useMemo(
		() =>
			scaleOrdinal({
				domain: Array.from(platforms),
				range: Array.from(platforms).map(
					(platform) => platformColorMap[platform],
				),
			}),
		[platforms],
	);

	const transformedData = useMemo(
		() =>
			Object.entries(posts)
				.reduce<Array<CustomBarGroup>>((acc, [key, value]) => {
					const failedValues = value[PostStatus.FAILED] ?? {};
					acc.push({
						date: key,
						failed: failedValues,
						published: value[PostStatus.PUBLISHED] ?? {},
					});
					return acc;
				}, [])
				.map((data) => {
					// fill in the missing platforms with 0
					const { failed, published } = data;
					const newData = { ...data };

					for (const platform of Array.from(platforms)) {
						const failedValue = failed[platform] ?? 0;
						const publishedValue = published[platform] ?? 0;
						newData[PostStatus.FAILED][platform] = failedValue;
						newData[PostStatus.PUBLISHED][platform] = publishedValue;
					}

					return newData;
				}),
		[posts, platforms],
	);

	let tooltipTimeout: number;

	return (
		<div className="chart-post-success">
			<div ref={containerRef} style={{ width: "100%", height: "400px" }}>
				{/* biome-ignore lint/a11y/noSvgWithoutTitle: title removed because of hover tooltip */}
				<svg width={width} height={height + margin.top} ref={tooltipRef}>
					<Group top={margin.top} width={width} height={height - margin.top}>
						<BarGroup
							data={transformedData}
							keys={[PostStatus.FAILED, PostStatus.PUBLISHED]}
							height={height - margin.top}
							width={width}
							x0={(x) => convert(LocalDate.parse(x.date)).toDate()}
							x0Scale={xScale}
							x1Scale={xScaleStatus}
							yScale={yMax}
							color={colorScaleGroup}
						>
							{(barGroups) =>
								barGroups.map((barGroup) => {
									const transformedBars: Array<CustomBarStack> =
										barGroup.bars.map((bar) => {
											const transformedBar: CustomBarStack = {
												status: bar.key,
											};
											for (const [platform, value] of Object.entries(
												bar.value,
											)) {
												transformedBar[platform as Platform] = value;
											}

											return transformedBar;
										});
									return (
										<Group
											width={width / transformedData.length}
											data-group="status-group"
											left={barGroup.x0}
											key={`bar-group-${barGroup.index}-${barGroup.x0}`}
										>
											<BarStack<CustomBarStack, Platform>
												data={transformedBars}
												key={`barGroup-${barGroup.index}-${barGroup.x0}`}
												keys={Array.from(platforms)}
												width={width / transformedData.length / 2}
												height={height - margin.top}
												x={() => barGroup.x0}
												xScale={xScaleStatus}
												color={(key) => colorScale(key)}
												yScale={yMax}
											>
												{(barStacks) =>
													barStacks.map((barStack) =>
														barStack.bars.map((bar) => {
															if (bar.height === 0) {
																return null;
															}
															const barWidth =
																width / transformedData.length / 2;
															const barGap = 4;
															return (
																// biome-ignore lint/a11y/noInteractiveElementToNoninteractiveRole: tooltip has hover
																<rect
																	role="tooltip"
																	data-bar={`${bar.key}-${bar.index}-${barStack.index}-${barStack.key}`}
																	key={`bar-stack-${barStack.index}-${bar.index}`}
																	x={
																		bar.index % 2 === 0
																			? bar.index * barWidth + barGap
																			: bar.index * barWidth
																	}
																	y={bar.y}
																	height={bar.height}
																	width={barWidth - barGap - 1}
																	fill={bar.color}
																	onMouseLeave={() => {
																		tooltipTimeout = window.setTimeout(() => {
																			hideTooltip();
																		}, 300);
																	}}
																	onMouseMove={(event) => {
																		if (tooltipTimeout)
																			clearTimeout(tooltipTimeout);
																		// TooltipInPortal expects coordinates to be relative to containerRef
																		// localPoint returns coordinates relative to the nearest SVG, which
																		// is what containerRef is set to in this example.
																		const eventSvgCoords = localPoint(event);
																		const left =
																			barGroup.x0 +
																			bar.index * barWidth +
																			barGap;
																		showTooltip({
																			tooltipData: bar,
																			tooltipTop: eventSvgCoords?.y,
																			tooltipLeft: left,
																		});
																	}}
																/>
															);
														}),
													)
												}
											</BarStack>
										</Group>
									);
								})
							}
						</BarGroup>
					</Group>
					<AxisLeft
						scale={yMax}
						tickFormat={(d) => `${d.toString()}`}
						tickLabelProps={{
							fill: "#000",
							fontSize: 12,
							textAnchor: "middle",
						}}
						tickValues={yMax.ticks().filter((tick) => Number.isInteger(tick))}
						top={margin.top}
						left={margin.left}
						stroke="transparent"
						tickStroke="transparent"
					/>
					<AxisBottom
						top={height - margin.top / 2}
						scale={xScale}
						tickFormat={(d) => {
							const date = LocalDate.ofInstant(
								Instant.ofEpochMilli(d.getTime()),
							);
							const month = date.monthValue().toString().padStart(2, "0");
							return `${date.dayOfMonth()}/${month}`;
						}}
						stroke={"transparent"}
						tickStroke={"transparent"}
						tickValues={dates.map((date) =>
							convert(LocalDate.parse(date)).toDate(),
						)}
						tickLabelProps={{
							fill: "black",
							fontSize: 10,
							textAnchor: "middle",
						}}
					/>
				</svg>
			</div>
			<LegendOrdinal
				className="legend"
				shapeWidth={18}
				shapeHeight={18}
				itemMargin={"1rem"}
				scale={legendScale}
				direction="row"
			/>
			{tooltipOpen && tooltipData && (
				<TooltipInPortal
					top={tooltipTop}
					left={tooltipLeft}
					style={tooltipStyles}
				>
					<p style={{ margin: 0 }}>
						{
							statusMap[
								tooltipData.bar.data.status as
									| PostStatus.PUBLISHED
									| PostStatus.FAILED
							]
						}{" "}
						<strong
							style={{
								textTransform: "capitalize",
								color: colorScale(tooltipData.key),
							}}
						>
							{tooltipData.key}
						</strong>{" "}
						posts: {tooltipData.bar.data[tooltipData.key]}
					</p>
				</TooltipInPortal>
			)}
		</div>
	);
};
